import { Select, SelectProps, Tooltip } from '@arco-design/web-react';
import React from 'react';
import styled from 'styled-components';

type LargeSelectProps = SelectProps & {
  /** 注意事项
   * 1. 通常用于展示大号的选择框
   * 2. 确保 FormItem (如果用了的话) 包含 field 属性
   * 3. 必须传入 value 和 onChange 出于受控模式才能正常工作
   */
  placeholderNode?: React.ReactNode;
  /** 是否为只读模式 */
  displayOnly?: boolean;
};

function StyledSelect(props: LargeSelectProps) {
  const { placeholderNode, value, onChange, displayOnly, ...selectProps } = props;

  const prefixNode = React.useMemo(() => {
    if (!value) {
      return placeholderNode;
    }
    return undefined;
  }, [value, placeholderNode]);

  // 确保当 value 为 undefined 时，传递给 Select 的值为空字符串
  const normalizedValue = value === undefined ? '' : value;
  /**
   * 当 value 为 undefined 时，Arco Design 的 Select 组件会将其视为"未设置"状态，而不是"清空"状态，
   * 因此它会继续显示上次选择的选项，同时由于 !undefined 为 true，你的 prefixNode 也被设置为了 placeholderNode。
   * 而当 value 为 '' 时，Select 组件将其视为明确的"空值"，因此清除了已选项，只显示 prefix。
   */
  if (displayOnly) {
    const selectedOption: any = props.options?.find((option: any) => {
      if (typeof option === 'object' && option !== null) {
        return option.value === normalizedValue;
      }
      return option === normalizedValue;
    });
    const { label, labelExtra, description, icon } = selectedOption.label.props;
    return (
      <DisplayOnlyWrapper>
        <IconLabel label={label} labelExtra={labelExtra} description={description} icon={icon} />
      </DisplayOnlyWrapper>
    );
  }

  return (
    <SelectWrapper
      prefix={prefixNode}
      value={normalizedValue}
      onChange={onChange}
      // getPopupContainer={(triggerNode) => triggerNode.parentElement}
      {...selectProps}
    />
  );
}
/** 大号的选择框,通常都有 ICON 一起展示 */
function IconLabel({
  label,
  labelExtra,
  description,
  icon,
  showTooltip,
  style = {},
}: {
  label: string;
  labelExtra?: React.ReactNode;
  description?: string;
  icon?: React.ReactNode;
  showTooltip?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <OptionItem style={style}>
      {icon && <LabelIcon>{icon}</LabelIcon>}
      <LabelItem>
        <LabelLayout>
          <span>{label}</span>
          {labelExtra}
        </LabelLayout>
        {showTooltip ? (
          <Tooltip content={description} position="bottom">
            <Content>{description}</Content>
          </Tooltip>
        ) : (
          <Content>{description}</Content>
        )}
      </LabelItem>
    </OptionItem>
  );
}

StyledSelect.displayName = 'LargeSelect';
export const LargeSelect = Object.assign(StyledSelect, {
  IconLabel,
});

/** ============================ 样式 ==================================== */
/** 大号的选择框 */
const SelectWrapper = styled(Select)`
  .arco-select-view {
    height: 52px !important;
  }
`;

/** 只读模式的包装器 */
const DisplayOnlyWrapper = styled.div`
  min-height: 52px;
  padding: 0px 8px;
  display: flex;
  align-items: center;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 52px;
`;

const LabelItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  line-height: 16px;
  padding: 8px;
  min-height: 52px;
`;

const LabelLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LabelIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.45;
`;

const Content = styled.div`
  color: #000;
  opacity: 0.45;
  font-size: 12px;
`;
